import { useTranslation } from "react-i18next";
import ReferenceDisplay from "../../../ui/content/ReferenceDisplay";
import EmptyState from "../../../ui/feedback/EmptyState";
import { Link } from "react-router";
import type { TermKind, TermUsage } from "../../../api/content/ontologyApi";
import { ITEM_KINDS } from "../../../api/content/reviewDecoders";
import { appPaths } from "../../../shared/routing/appPaths";
import TermDataUses from "../../knowledgeData/lenses/TermDataUses";
import { itemKindText } from "../../workflows/inputCards/typeWords";

/** The kinds instance data uses: classes type its individuals, properties make its statements. */
const KINDS_IN_THE_DATA: ReadonlySet<TermKind> = new Set(["class", "object_property", "datatype_property", "annotation_property"]);

interface TermUsageListProps {
  readonly usage: readonly TermUsage[];
  readonly projectId: string;
  readonly term: { readonly iri: string; readonly kind: TermKind };
}

/**
 * Where the term is used at this version: shapes, competency questions, examples, label
 * lists, other modules, and steps or Workflows through what they pin (the same reading
 * as the review's conflict check). Each entry opens the exact item version. Then "In the
 * data" (F84 §9): the results whose individuals or statements use the term.
 */
export default function TermUsageList({ usage, projectId, term }: TermUsageListProps) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      {usage.length === 0 ? <EmptyState>{t("ontologyViewer.usage.none")}</EmptyState> : (
        <div className="space-y-2">
          {ITEM_KINDS.map((kind) => {
            const ofKind = usage.filter((entry) => entry.kind === kind);
            if (ofKind.length === 0) {
              return null;
            }
            return (
              <div key={kind}>
                <p className="app-label text-xs">{itemKindText(kind, t)}</p>
                <ul className="space-y-0.5">
                  {ofKind.map((entry) => (
                    <li key={entry.reference} className="text-sm">
                      <Link className="app-action-link" to={appPaths.item(entry.reference)} title={entry.reference}>
                        {entry.name}
                      </Link>
                      {entry.via !== null ? (
                        <span className="app-text-muted ml-2 text-xs">
                          {t("ontologyViewer.usage.via")}{" "}
                          <ReferenceDisplay kind="itemId" value={entry.via} href={appPaths.item(entry.via)} copy={false} />
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
      {KINDS_IN_THE_DATA.has(term.kind) ? (
        <div>
          <p className="app-label text-xs">{t("ontologyViewer.usage.inTheData")}</p>
          <TermDataUses key={term.iri} projectId={projectId} iri={term.iri} />
        </div>
      ) : null}
    </div>
  );
}
